/**
 * نگهبان نسخه‌ی برنامه — دو حالت:
 *
 * ۱) تغییر config.Version  (کاربرد عادی، از جمله بیلدهای توسعه/طراحی):
 *    فقط کش‌های «داده/API» و state ذخیره‌شده پاک می‌شوند. کش فایل‌های
 *    استاتیک/طراحی (پیش‌کش workbox) دست‌نخورده می‌ماند و اگر واقعاً بیلد
 *    جدیدی منتشر شده باشد از مسیر عادی سرویس‌ورکر تازه می‌شود. ری‌لود اجباری نداریم.
 *
 * ۲) تغییر config.PurgeToken  (پاک‌سازی سراسری اجباری — با فلگ سمت سرور):
 *    «همه‌ی» کش‌ها بدون توجه به مدت‌شان پاک می‌شوند + یک ری‌لود.
 *    ادمین برای اجبارِ همه‌ی کاربران به نسخه‌ی تازه، فقط مقدار PurgeToken را
 *    عوض می‌کند (هر رشته‌ی جدید، مثلاً تاریخ). چون با مقدار مقایسه می‌شود،
 *    idempotent است و «فراموشیِ خاموش‌کردن فلگ» مشکلی ایجاد نمی‌کند.
 *
 * کلیدهای دیگر localStorage (تم، همین کلید نسخه) دست‌نخورده می‌مانند.
 */

const VERSION_KEY = "bndbs:appVersion";
const PURGE_KEY = "bndbs:purgeToken";
const RELOAD_FLAG = "bndbs:versionReloading";

// کش‌های «داده/API» که با تغییر نسخه پاک می‌شوند (استاتیک/طراحی جزوشان نیست)
const DATA_CACHE_RE = /(api-get|appsetting|bndbs-api)/i;

function readLS(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}
function writeLS(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        /* noop */
    }
}
function readSS(key) {
    try {
        return sessionStorage.getItem(key);
    } catch (e) {
        return null;
    }
}
function writeSS(key, value) {
    try {
        sessionStorage.setItem(key, value);
    } catch (e) {
        /* noop */
    }
}
function clearReloadFlag() {
    try {
        sessionStorage.removeItem(RELOAD_FLAG);
    } catch (e) {
        /* noop */
    }
}

async function deleteCaches(filter) {
    try {
        if (typeof caches === "undefined" || !caches.keys) return;
        const names = await caches.keys();
        await Promise.all(
            names.filter((n) => (filter ? filter(n) : true)).map((n) => caches.delete(n))
        );
    } catch (e) {
        /* noop */
    }
}

async function askServiceWorkerToUpdate() {
    try {
        if (navigator.serviceWorker) {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg) await reg.update();
        }
    } catch (e) {
        /* noop */
    }
}

/**
 * @param {object} config store.state.config (باید Version و در صورت وجود PurgeToken داشته باشد)
 * @param {() => void} [onWipe] پاک‌کردن state ذخیره‌شده (clearPersistedState)
 * @returns {Promise<boolean>} true یعنی ری‌لود آغاز شده؛ ادامه‌ی bootstrap را متوقف کن.
 */
export async function enforceAppVersion(config, onWipe) {
    config = config || {};
    const version =
        config.Version === undefined || config.Version === null
            ? ""
            : String(config.Version);
    const purgeToken =
        config.PurgeToken === undefined || config.PurgeToken === null
            ? ""
            : String(config.PurgeToken);

    const storedVersion = readLS(VERSION_KEY);
    const storedPurge = readLS(PURGE_KEY);
    const alreadyReloaded = readSS(RELOAD_FLAG) === "1";

    // ---- حالت ۲: پاک‌سازی کامل اجباری (PurgeToken عوض شده) ----
    if (purgeToken && storedPurge !== null && storedPurge !== purgeToken) {
        await deleteCaches(); // همه‌ی کش‌ها، بدون فیلتر
        if (typeof onWipe === "function") {
            try {
                onWipe();
            } catch (e) {
                /* noop */
            }
        }
        writeLS(PURGE_KEY, purgeToken);
        if (version) writeLS(VERSION_KEY, version);

        if (!alreadyReloaded) {
            writeSS(RELOAD_FLAG, "1");
            try {
                window.location.reload();
                return true;
            } catch (e) {
                return false;
            }
        }
        clearReloadFlag();
        return false;
    }

    // نخستین اجرا روی این مرورگر → فقط ثبت کن
    if (storedVersion === null) {
        if (version) writeLS(VERSION_KEY, version);
        if (purgeToken) writeLS(PURGE_KEY, purgeToken);
        clearReloadFlag();
        return false;
    }
    if (storedPurge === null && purgeToken) writeLS(PURGE_KEY, purgeToken);

    // ---- حالت ۱: تغییر نسخه‌ی عادی → فقط کش‌های داده/API + state ----
    if (version && storedVersion !== version) {
        await deleteCaches((n) => DATA_CACHE_RE.test(n));
        if (typeof onWipe === "function") {
            try {
                onWipe();
            } catch (e) {
                /* noop */
            }
        }
        writeLS(VERSION_KEY, version);
        // اگر بیلد فرانت هم واقعاً عوض شده، سرویس‌ورکر خودش تشخیص می‌دهد
        askServiceWorkerToUpdate();
    }

    clearReloadFlag();
    return false;
}

export default { enforceAppVersion };
